import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface Category {
  icon: string;
  title: string;
  description: string;
  count: number;
  path: string;
  accent: string;
  glow: string;
}

const categories: Category[] = [
  {
    icon: '🌟',
    title: 'Натальная карта',
    description: 'Карта неба на момент рождения — основа всех прогнозов и анализов',
    count: 1,
    path: '/natal',
    accent: '#d4af37',
    glow: 'rgba(212, 175, 55, 0.4)',
  },
  {
    icon: '⏰',
    title: 'Прогнозы по времени',
    description: 'От дня до года — знайте, что готовит каждый период вашей жизни',
    count: 4,
    path: '/forecasts',
    accent: '#F4E4A6',
    glow: 'rgba(244, 228, 166, 0.4)',
  },
  {
    icon: '💼',
    title: 'Сферы жизни',
    description: 'Любовь, карьера, семья, здоровье — прогноз по ключевым областям',
    count: 4,
    path: '/forecasts',
    accent: '#FF9800',
    glow: 'rgba(255, 152, 0, 0.4)',
  },
  {
    icon: '🪐',
    title: 'Астрологические методы',
    description: 'Транзиты, прогрессии, соляр, лунар, затмения — полная картина влияний',
    count: 6,
    path: '/forecasts',
    accent: '#9B59B6',
    glow: 'rgba(155, 89, 182, 0.4)',
  },
  {
    icon: '🔢',
    title: 'Нумерология',
    description: 'Персональные циклы, числа силы и счастливые даты для важных решений',
    count: 5,
    path: '/numerology',
    accent: '#3498DB',
    glow: 'rgba(52, 152, 219, 0.4)',
  },
  {
    icon: '💫',
    title: 'Совместимость',
    description: 'Синастрия и композитная карта — глубинный анализ отношений двух людей',
    count: 2,
    path: '/compatibility',
    accent: '#E91E63',
    glow: 'rgba(233, 30, 99, 0.4)',
  },
  {
    icon: '🐉',
    title: 'Кармические прогнозы',
    description: 'Прошлые жизни, кармические узлы и путь предназначения вашей души',
    count: 3,
    path: '/forecasts',
    accent: '#673AB7',
    glow: 'rgba(103, 58, 183, 0.4)',
  },
  {
    icon: '💎',
    title: 'Спец. анализы',
    description: 'Таланты, тень, потенциалы, генограмма — глубинные слои личности',
    count: 8,
    path: '/analyses',
    accent: '#7dd3c0',
    glow: 'rgba(125, 211, 192, 0.4)',
  },
  {
    icon: '📅',
    title: 'Выбор даты',
    description: 'Элективная астрология — найдите идеальное время для важных событий',
    count: 1,
    path: '/electional',
    accent: '#009688',
    glow: 'rgba(0, 150, 136, 0.4)',
  },
  {
    icon: '🏥',
    title: 'Медицинский прогноз',
    description: 'Здоровье и уязвимые места по натальной карте — профилактика и ресурсы',
    count: 1,
    path: '/forecasts/medical',
    accent: '#4CAF50',
    glow: 'rgba(76, 175, 80, 0.4)',
  },
  {
    icon: '👶',
    title: 'Детский гороскоп',
    description: 'Натальная карта и прогноз для ребёнка — понимание с ранних лет',
    count: 1,
    path: '/forecasts/child',
    accent: '#FF6B9D',
    glow: 'rgba(255, 107, 157, 0.4)',
  },
  {
    icon: '🎯',
    title: 'Профориентация',
    description: 'Профессии и призвание по натальной карте — реализуйте свой потенциал',
    count: 1,
    path: '/forecasts/vocational',
    accent: '#FF5722',
    glow: 'rgba(255, 87, 34, 0.4)',
  },
];

const CARD_WIDTH = 280;
const GAP = 20;
const AUTO_SCROLL_SPEED = 0.5;

const PredictionCarousel: React.FC = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const scrollPosRef = useRef(0);
  const isPausedRef = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const totalWidth = categories.length * (CARD_WIDTH + GAP);

  const autoScroll = useCallback(() => {
    if (!scrollRef.current) return;

    if (!isPausedRef.current) {
      scrollPosRef.current += AUTO_SCROLL_SPEED;
      if (scrollPosRef.current >= totalWidth / 2) {
        scrollPosRef.current = 0;
      }
      scrollRef.current.scrollLeft = scrollPosRef.current;
    }

    animFrameRef.current = requestAnimationFrame(autoScroll);
  }, [totalWidth]);

  useEffect(() => {
    animFrameRef.current = requestAnimationFrame(autoScroll);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [autoScroll]);

  const handleMouseEnter = () => {
    isPausedRef.current = true;
  };

  const handleMouseLeave = () => {
    isPausedRef.current = false;
    setHoveredIndex(null);
    setMousePos(null);
    setTilt({ x: 0, y: 0 });
  };

  const handleCardMouseMove = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * -12, y: x * 12 });
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setHoveredIndex(index);
  };

  const handleCardMouseLeave = () => {
    setHoveredIndex(null);
    setMousePos(null);
    setTilt({ x: 0, y: 0 });
  };

  const doubledCategories = [...categories, ...categories];

  return (
    <section ref={sectionRef} style={sectionStyle}>
      <style>{`
        @keyframes pc-fadeInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pc-rotateBorder {
          0% { --pc-angle: 0deg; }
          100% { --pc-angle: 360deg; }
        }
        @keyframes pc-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-6px) rotate(2deg); }
          75% { transform: translateY(3px) rotate(-1deg); }
        }
        @keyframes pc-pulseGlow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        @keyframes pc-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes pc-starFloat {
          0% { transform: translateY(0) translateX(0) scale(1); opacity: 0.6; }
          50% { transform: translateY(-20px) translateX(10px) scale(1.3); opacity: 1; }
          100% { transform: translateY(-40px) translateX(-5px) scale(0.8); opacity: 0; }
        }
        @keyframes pc-cardEnter {
          from { opacity: 0; transform: translateY(60px) scale(0.85) rotateX(15deg); }
          to { opacity: 1; transform: translateY(0) scale(1) rotateX(0deg); }
        }
        @keyframes pc-countPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes pc-gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .pc-scroll::-webkit-scrollbar { display: none; }
        .pc-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Заголовок */}
      <div style={{
        textAlign: 'center',
        marginBottom: '48px',
        ...(isVisible ? { animation: 'pc-fadeInUp 0.8s ease-out' } : { opacity: 0 }),
      }}>
        <h2 style={headingStyle}>
          Каталог предсказаний
        </h2>
        <p style={subheadingStyle}>
          {categories.length} направлений • Более 40 видов анализов и прогнозов
        </p>
      </div>

      {/* Градиентные фейды по краям */}
      <div style={fadeLeftStyle} />
      <div style={fadeRightStyle} />

      {/* Карусель */}
      <div
        style={carouselContainerStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={scrollRef} className="pc-scroll" style={scrollTrackStyle}>
          {doubledCategories.map((cat, index) => {
            const realIndex = index % categories.length;
            const isHovered = hoveredIndex === realIndex;

            return (
              <div
                key={index}
                style={{
                  ...cardStyle,
                  ...(isVisible ? {
                    animation: `pc-cardEnter 0.7s ease-out ${realIndex * 0.08}s both`,
                  } : { opacity: 0 }),
                  ...(isHovered ? {
                    transform: `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(1.05) translateY(-8px)`,
                    borderColor: cat.accent,
                    boxShadow: `0 20px 60px ${cat.glow}, 0 0 40px ${cat.glow}, inset 0 0 30px ${cat.glow}`,
                  } : {
                    transform: 'perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  }),
                }}
                onMouseMove={(e) => handleCardMouseMove(e, realIndex)}
                onMouseLeave={handleCardMouseLeave}
                onClick={() => navigate(cat.path)}
              >
                {/* Анимированная градиентная рамка */}
                <div style={{
                  ...borderGlowStyle,
                  background: `linear-gradient(${isHovered ? '135deg' : '180deg'}, ${cat.accent}40, transparent 60%)`,
                  opacity: isHovered ? 1 : 0.5,
                }} />

                {/* Фоновое свечение при hover */}
                {isHovered && mousePos && (
                  <div style={{
                    position: 'absolute',
                    left: mousePos.x - 80,
                    top: mousePos.y - 80,
                    width: 160,
                    height: 160,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${cat.glow} 0%, transparent 70%)`,
                    pointerEvents: 'none',
                    transition: 'left 0.1s, top 0.1s',
                  }} />
                )}

                {/* Цветная полоска сверху */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '10%',
                  right: '10%',
                  height: '2px',
                  background: `linear-gradient(90deg, transparent, ${cat.accent}, transparent)`,
                  borderRadius: '0 0 2px 2px',
                  ...(isHovered ? {
                    height: '3px',
                    left: '5%',
                    right: '5%',
                    boxShadow: `0 0 12px ${cat.glow}`,
                  } : {}),
                  transition: 'all 0.3s ease',
                }} />

                {/* Иконка */}
                <div style={{
                  ...iconContainerStyle,
                  ...(isHovered ? {
                    animation: 'pc-float 2s ease-in-out infinite',
                    boxShadow: `0 8px 30px ${cat.glow}`,
                  } : {}),
                }}>
                  <span style={{ fontSize: '32px' }}>{cat.icon}</span>
                  {/* Пульсирующее свечение за иконкой */}
                  <div style={{
                    position: 'absolute',
                    inset: '-4px',
                    borderRadius: '50%',
                    border: `1px solid ${cat.accent}`,
                    opacity: isHovered ? 0.6 : 0,
                    animation: isHovered ? 'pc-pulseGlow 2s ease-in-out infinite' : 'none',
                  }} />
                </div>

                {/* Контент */}
                <h3 style={{
                  ...cardTitleStyle,
                  color: isHovered ? cat.accent : '#f0f0f0',
                  transition: 'color 0.3s ease',
                }}>
                  {cat.title}
                </h3>
                <p style={cardDescStyle}>{cat.description}</p>

                {/* Количество видов внизу */}
                <div style={{
                  ...countTextStyle,
                  color: isHovered ? cat.accent : 'rgba(212, 175, 55, 0.5)',
                  borderColor: isHovered ? `${cat.accent}40` : 'rgba(212, 175, 55, 0.12)',
                }}>
                  {cat.count === 1 ? '1 направление' : `${cat.count} направления`}
                </div>

                {/* Декоративные частицы при hover */}
                {isHovered && (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} style={{
                        position: 'absolute',
                        width: '4px',
                        height: '4px',
                        borderRadius: '50%',
                        background: cat.accent,
                        left: `${20 + i * 30}%`,
                        bottom: '10%',
                        animation: `pc-starFloat ${1.5 + i * 0.3}s ease-in-out infinite ${i * 0.2}s`,
                        pointerEvents: 'none',
                      }} />
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// === Styles ===

const sectionStyle: React.CSSProperties = {
  padding: '32px 0',
  maxWidth: '1400px',
  margin: '0 auto',
  position: 'relative',
  overflow: 'hidden',
};

const headingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontSize: '42px',
  background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
  backgroundSize: '200% auto',
  animation: 'pc-gradientFlow 4s ease infinite',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  margin: '0 0 12px',
  letterSpacing: '0.02em',
};

const subheadingStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '16px',
  color: 'rgba(212, 175, 55, 0.6)',
  margin: 0,
  letterSpacing: '0.05em',
};

const carouselContainerStyle: React.CSSProperties = {
  position: 'relative',
  padding: '20px 0',
};

const scrollTrackStyle: React.CSSProperties = {
  display: 'flex',
  gap: '20px',
  overflowX: 'auto',
  padding: '20px 60px',
  scrollBehavior: 'auto',
};

const cardStyle: React.CSSProperties = {
  flex: '0 0 280px',
  width: 280,
  minHeight: 320,
  background: 'rgba(13, 20, 40, 0.85)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.12)',
  borderRadius: '20px',
  padding: '32px 24px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  position: 'relative',
  cursor: 'pointer',
  transition: 'transform 0.15s ease-out, box-shadow 0.3s ease, border-color 0.3s ease',
  overflow: 'hidden',
};

const borderGlowStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  borderRadius: '20px',
  pointerEvents: 'none',
  transition: 'opacity 0.3s ease, background 0.3s ease',
};

const iconContainerStyle: React.CSSProperties = {
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  background: 'rgba(18, 15, 46, 0.8)',
  border: '1px solid rgba(212, 175, 55, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
  position: 'relative',
  transition: 'box-shadow 0.3s ease',
};

const countTextStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.03em',
  padding: '6px 14px',
  borderRadius: '20px',
  border: '1px solid',
  marginTop: 'auto',
  transition: 'color 0.3s ease, border-color 0.3s ease',
};

const cardTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontStyle: 'italic',
  fontSize: '19px',
  fontWeight: 600,
  margin: '0 0 10px',
  lineHeight: 1.3,
  transition: 'color 0.3s ease',
};

const cardDescStyle: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '13px',
  color: '#999',
  lineHeight: 1.6,
  margin: 0,
  flexGrow: 1,
};

const fadeLeftStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '80px',
  background: 'linear-gradient(to right, rgba(10, 8, 20, 1) 0%, transparent 100%)',
  pointerEvents: 'none',
  zIndex: 2,
};

const fadeRightStyle: React.CSSProperties = {
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  width: '80px',
  background: 'linear-gradient(to left, rgba(10, 8, 20, 1) 0%, transparent 100%)',
  pointerEvents: 'none',
  zIndex: 2,
};

export default PredictionCarousel;
